import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { createVisit, getVmsSettings } from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { notify } from '../../../../utils/notification';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_VISITS,
    SOCIETY_PORTAL_VISITS_VIEW,
} from '../../../../Routing/routes';
import FormSection from '../components/form/FormSection';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import { labelCls, errorCls, hintCls, controlCls, numberCls } from '../components/form/formStyles';
import {
    PURPOSES,
    VEHICLE_TYPES,
    toOptions,
    toLocalInput,
} from '../vmsConstants';
import { VISITOR_TYPES, typeLabel, normalizePhone, isValidPhone, isValidEmail, LIMITS } from '../visitorConstants';

const PURPOSE_OPTIONS = toOptions(PURPOSES);
const VEHICLE_OPTIONS = toOptions(VEHICLE_TYPES);
const VISITOR_TYPE_OPTIONS = VISITOR_TYPES.map(t => ({ value: t, label: typeLabel(t) }));

const EMPTY = {
    visitor_name: '',
    visitor_phone: '',
    visitor_email: '',
    visitor_type: 'GUEST',
    company_name: '',
    wing_id: '',
    flat_id: '',
    purpose: 'PERSONAL',
    purpose_detail: '',
    entry_gate: '',
    expected_at: '',
    vehicle_number: '',
    vehicle_type: '',
    companion_count: 0,
    photo_url: '',
    remarks: '',
};

const AddVisit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const saving = useSelector(state => state.VmsReducer?.visits?.saving);
    // max_companion_count and visitor_photo_required are stored but never read
    // by the backend, so they are policy only if this form applies them.
    const settings = useSelector(state => state.VmsReducer?.settings?.data);
    const maxCompanions = settings?.max_companion_count;
    const photoRequired = settings?.visitor_photo_required === true || settings?.visitor_photo_required === 1;

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (societyId && !settings) dispatch(getVmsSettings({ societyId }));
    }, [dispatch, societyId, settings]);

    const set = (k, v) => {
        setForm(f => ({ ...f, [k]: v }));
        setErrors(e => ({ ...e, [k]: undefined }));
    };

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITS);

    const validate = () => {
        const e = {};
        if (!form.visitor_name.trim()) e.visitor_name = 'Visitor name is required';
        if (!form.visitor_phone.trim()) e.visitor_phone = 'Phone number is required';
        else if (!isValidPhone(form.visitor_phone)) e.visitor_phone = 'Enter a 10-digit mobile number, or a full number with country code';
        if (form.visitor_email.trim() && !isValidEmail(form.visitor_email)) e.visitor_email = 'Enter a valid email address';
        if (!form.wing_id) e.wing_id = 'Wing is required';
        if (!form.flat_id) e.flat_id = 'Flat is required';
        if (Number(form.companion_count) < 0) e.companion_count = 'Cannot be negative';
        else if (maxCompanions != null && Number(form.companion_count) > Number(maxCompanions)) {
            e.companion_count = `Your society allows at most ${maxCompanions}`;
        }
        if (photoRequired && !form.photo_url.trim()) {
            e.photo_url = 'Your society requires a visitor photo';
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        if (!societyId) {
            notify('error', 'No society selected. Pick a society before logging a visit.');
            return;
        }

        // Assembled from an explicit list: Joi does not strip unknown keys, and
        // blanks are dropped so the server's own defaults apply.
        const payload = {
            society_id: Number(societyId),
            visitor_name: form.visitor_name.trim(),
            // The visitor is looked up or created by (phone, society), so the
            // same normalisation the visitor master uses has to apply here or a
            // second record appears for the same person.
            visitor_phone: normalizePhone(form.visitor_phone),
            wing_id: Number(form.wing_id),
            flat_id: Number(form.flat_id),
            visitor_type: form.visitor_type,
            purpose: form.purpose,
            companion_count: Number(form.companion_count) || 0,
        };
        [
            ['visitor_email', form.visitor_email], ['company_name', form.company_name],
            ['purpose_detail', form.purpose_detail], ['entry_gate', form.entry_gate],
            ['vehicle_number', form.vehicle_number], ['photo_url', form.photo_url],
            ['remarks', form.remarks],
        ].forEach(([k, v]) => { if (v && v.trim()) payload[k] = v.trim(); });
        if (form.vehicle_type) payload.vehicle_type = form.vehicle_type;
        if (form.expected_at) payload.expected_at = new Date(form.expected_at).toISOString();

        dispatch(createVisit({
            ...payload,
            onSuccess: (res) => {
                const visit = res?.data;
                // The server picks the initial status from the block flag, a
                // matching pre-approval, a gate pass, then the society's entry
                // mode — so the detail screen is where the operator finds out
                // what actually happened and what to do next.
                if (visit?.visit_id) {
                    navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_VISITS_VIEW.replace(':id', visit.visit_id));
                } else {
                    backToList();
                }
            },
        }));
    };

    const unwrap = (v) => (v === 'all' ? '' : v);

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-w-full">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Visits
                </button>

                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h1 className="tw-mb-0 tw-text-base tw-font-bold tw-text-gray-800">Log a Visit</h1>
                        <p className="tw-mb-0 tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            The visitor is matched by phone, or created if new
                        </p>
                    </div>

                    <div className="tw-p-5">
                        {/* Sets expectations before submit: the operator does not
                            choose the status, the server does. */}
                        <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2.5 tw-mb-4 tw-rounded-md tw-bg-sky-50 tw-border tw-border-sky-100 tw-text-[11px] tw-text-sky-900">
                            <Icon icon="mdi:information-outline" className="tw-text-sm tw-shrink-0 tw-mt-px" />
                            <span>
                                What happens next is decided on save: a matching pre-approval or gate pass approves the
                                visit outright, a society set to direct entry checks the visitor straight in, and
                                anything else waits for the resident. A blocked visitor is refused.
                            </span>
                        </div>

                        <form onSubmit={handleSubmit} className="tw-space-y-5">
                            <FormSection icon="mdi:account-outline" title="Visitor" hint="Matched on phone against your society's existing visitors">
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                                    <div className="sm:tw-col-span-2">
                                        <label className={labelCls}>Name <span className="tw-text-rose-500">*</span></label>
                                        <input
                                            type="text"
                                            value={form.visitor_name}
                                            onChange={e => set('visitor_name', e.target.value)}
                                            maxLength={LIMITS.name}
                                            placeholder="e.g. Rajesh Kumar"
                                            className={controlCls(!!errors.visitor_name)}
                                        />
                                        {errors.visitor_name && <p className={errorCls}>{errors.visitor_name}</p>}
                                    </div>
                                    <div>
                                        <label className={labelCls}>Visitor type</label>
                                        <SearchableDropdown
                                            small
                                            clearable={false}
                                            value={form.visitor_type}
                                            onChange={(v) => set('visitor_type', v)}
                                            options={VISITOR_TYPE_OPTIONS}
                                            placeholder="Select type"
                                        />
                                    </div>
                                </div>

                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mt-3">
                                    <div>
                                        <label className={labelCls}>Phone <span className="tw-text-rose-500">*</span></label>
                                        <input
                                            type="tel"
                                            inputMode="tel"
                                            value={form.visitor_phone}
                                            onChange={e => set('visitor_phone', e.target.value)}
                                            maxLength={LIMITS.phone}
                                            placeholder="9876543210"
                                            className={`${controlCls(!!errors.visitor_phone)} tw-font-mono`}
                                        />
                                        {errors.visitor_phone && <p className={errorCls}>{errors.visitor_phone}</p>}
                                    </div>
                                    <div>
                                        <label className={labelCls}>Email</label>
                                        <input
                                            type="email"
                                            value={form.visitor_email}
                                            onChange={e => set('visitor_email', e.target.value)}
                                            maxLength={LIMITS.email}
                                            placeholder="optional"
                                            className={controlCls(!!errors.visitor_email)}
                                        />
                                        {errors.visitor_email && <p className={errorCls}>{errors.visitor_email}</p>}
                                    </div>
                                    <div>
                                        <label className={labelCls}>Company</label>
                                        <input
                                            type="text"
                                            value={form.company_name}
                                            onChange={e => set('company_name', e.target.value)}
                                            maxLength={LIMITS.company_name}
                                            placeholder="optional"
                                            className={controlCls(false)}
                                        />
                                    </div>
                                </div>
                            </FormSection>

                            <FormSection icon="mdi:home-outline" title="Visiting" hint="A pre-approval or gate pass only matches for the flat it was issued against">
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
                                    <WingFlatPicker
                                        societyId={societyId}
                                        wingId={form.wing_id}
                                        flatId={form.flat_id}
                                        onWingChange={(v) => set('wing_id', v)}
                                        onFlatChange={(v) => set('flat_id', v)}
                                        errors={errors}
                                    />
                                    <div>
                                        <label className={labelCls}>Purpose</label>
                                        <SearchableDropdown
                                            small
                                            clearable={false}
                                            value={form.purpose}
                                            onChange={(v) => set('purpose', v)}
                                            options={PURPOSE_OPTIONS}
                                            placeholder="Select purpose"
                                        />
                                    </div>
                                </div>

                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3 tw-mt-3">
                                    <div className="sm:tw-col-span-2">
                                        <label className={labelCls}>Purpose detail</label>
                                        <input
                                            type="text"
                                            value={form.purpose_detail}
                                            onChange={e => set('purpose_detail', e.target.value)}
                                            maxLength={255}
                                            placeholder="e.g. Kitchen sink repair"
                                            className={controlCls(false)}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Expected at</label>
                                        <input
                                            type="datetime-local"
                                            value={toLocalInput(form.expected_at) || form.expected_at}
                                            onChange={e => set('expected_at', e.target.value)}
                                            className={controlCls(false)}
                                        />
                                    </div>
                                </div>
                            </FormSection>

                            <FormSection icon="mdi:gate" title="At the gate">
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-3">
                                    <div>
                                        <label className={labelCls}>Entry gate</label>
                                        <input
                                            type="text"
                                            value={form.entry_gate}
                                            onChange={e => set('entry_gate', e.target.value)}
                                            maxLength={50}
                                            placeholder="e.g. Main Gate"
                                            className={controlCls(false)}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Vehicle number</label>
                                        <input
                                            type="text"
                                            value={form.vehicle_number}
                                            onChange={e => set('vehicle_number', e.target.value.toUpperCase())}
                                            maxLength={20}
                                            placeholder="MH12AB1234"
                                            className={`${controlCls(false)} tw-font-mono`}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Vehicle type</label>
                                        <SearchableDropdown
                                            small
                                            value={form.vehicle_type}
                                            onChange={(v) => set('vehicle_type', unwrap(v))}
                                            options={VEHICLE_OPTIONS}
                                            placeholder="None"
                                        />
                                    </div>
                                    <div>
                                        <label className={labelCls}>Companions</label>
                                        <input
                                            type="number"
                                            min={0}
                                            value={form.companion_count}
                                            onChange={e => set('companion_count', e.target.value)}
                                            className={numberCls(!!errors.companion_count)}
                                        />
                                        {errors.companion_count
                                            ? <p className={errorCls}>{errors.companion_count}</p>
                                            : <p className={hintCls}>People accompanying the visitor</p>}
                                    </div>
                                </div>

                                <div className="tw-mt-3">
                                    <label className={labelCls}>
                                        Visitor photo URL
                                        {photoRequired && <span className="tw-text-rose-500"> *</span>}
                                    </label>
                                    <input
                                        type="url"
                                        value={form.photo_url}
                                        onChange={e => set('photo_url', e.target.value)}
                                        maxLength={500}
                                        placeholder="https://..."
                                        className={controlCls(!!errors.photo_url)}
                                    />
                                    {errors.photo_url
                                        ? <p className={errorCls}>{errors.photo_url}</p>
                                        : <p className={hintCls}>
                                            {photoRequired
                                                ? 'Required by your society settings — upload the photo elsewhere and paste the link'
                                                : 'Optional — upload elsewhere, paste the link here'}
                                        </p>}
                                </div>

                                <div className="tw-mt-3">
                                    <label className={labelCls}>Remarks</label>
                                    <textarea
                                        rows={2}
                                        value={form.remarks}
                                        onChange={e => set('remarks', e.target.value)}
                                        maxLength={1000}
                                        placeholder="Anything the next shift should know"
                                        className={`${controlCls(false)} tw-resize-none`}
                                    />
                                </div>
                            </FormSection>

                            <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-pt-3 tw-border-t tw-border-gray-100">
                                <button
                                    type="button"
                                    onClick={backToList}
                                    className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700 tw-rounded-md disabled:tw-opacity-50"
                                >
                                    <Icon icon="mdi:door-open" className="tw-text-base" />
                                    {saving ? 'Logging...' : 'Log Visit'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddVisit;
